import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const QuickStartWizard = ({ onComplete, onSkip }) => {
  const [wizardStep, setWizardStep] = useState(0);
  const [familyData, setFamilyData] = useState({
    familyName: '',
    members: [
      { name: '', role: '', color: '#EF4444' }
    ]
  });

  const colorOptions = [
    { value: '#EF4444', label: 'Red' },
    { value: '#3B82F6', label: 'Blue' },
    { value: '#EC4899', label: 'Pink' },
    { value: '#10B981', label: 'Green' },
    { value: '#F59E0B', label: 'Orange' },
    { value: '#8B5CF6', label: 'Purple' },
    { value: '#06B6D4', label: 'Cyan' },
    { value: '#F43F5E', label: 'Rose' }
  ];

  const wizardSteps = [
    {
      title: 'Family Name',
      description: 'What should we call your family?',
      icon: 'home'
    },
    {
      title: 'Add Members',
      description: 'Add your family members and assign colors',
      icon: 'users'
    },
    {
      title: 'All Set!',
      description: 'Your family profile is ready',
      icon: 'check-circle'
    }
  ];

  const handleAddMember = () => {
    setFamilyData({
      ...familyData,
      members: [...familyData?.members, { name: '', role: '', color: colorOptions?.[familyData?.members?.length % colorOptions?.length]?.value }]
    });
  };

  const handleRemoveMember = (index) => {
    const newMembers = familyData?.members?.filter((_, i) => i !== index);
    setFamilyData({ ...familyData, members: newMembers });
  };

  const handleMemberChange = (index, field, value) => {
    const newMembers = [...familyData?.members];
    newMembers[index] = { ...newMembers?.[index], [field]: value };
    setFamilyData({ ...familyData, members: newMembers });
  };

  const handleNext = () => {
    if (wizardStep < wizardSteps?.length - 1) {
      setWizardStep(wizardStep + 1);
    } else {
      // Save to localStorage
      localStorage?.setItem('familySetupComplete', 'true');
      localStorage?.setItem('familyName', familyData?.familyName);
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (wizardStep > 0) {
      setWizardStep(wizardStep - 1);
    }
  };

  const canProceed = () => {
    if (wizardStep === 0) {
      return familyData?.familyName?.trim()?.length > 0;
    }
    if (wizardStep === 1) {
      return familyData?.members?.every(m => m?.name?.trim()?.length > 0 && m?.role?.trim()?.length > 0);
    }
    return true;
  };

  const currentWizardStep = wizardSteps?.[wizardStep];

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-card rounded-xl shadow-warm-lg p-8">
        {/* Wizard Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-blue-600 rounded-full mb-4 shadow-warm-lg">
            <Icon name={currentWizardStep?.icon} size={32} className="text-white" />
          </div>
          <h2 className="text-h2 font-heading text-gray-900 mb-2">{currentWizardStep?.title}</h2>
          <p className="text-gray-600">{currentWizardStep?.description}</p>
        </div>

        {/* Wizard Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {wizardSteps?.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all ${
                index === wizardStep
                  ? 'w-12 bg-blue-600'
                  : index < wizardStep
                  ? 'w-8 bg-emerald-500' :'w-8 bg-gray-300'
              }`}
            />
          ))}
        </div>

        {/* Wizard Content */}
        <div className="min-h-[300px]">
          {wizardStep === 0 && (
            <div className="space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-gray-700 mb-2 block">Family Name</span>
                <Input
                  type="text"
                  placeholder="e.g., The Johnsons"
                  value={familyData?.familyName}
                  onChange={(e) => setFamilyData({ ...familyData, familyName: e?.target?.value })}
                  className="w-full"
                />
              </label>
              <p className="text-sm text-gray-500">
                This will be displayed throughout the app to personalize your experience.
              </p>
            </div>
          )}

          {wizardStep === 1 && (
            <div className="space-y-4">
              {familyData?.members?.map((member, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Member {index + 1}</span>
                    {familyData?.members?.length > 1 && (
                      <button
                        onClick={() => handleRemoveMember(index)}
                        className="text-red-500 hover:text-red-700 transition-colors"
                      >
                        <Icon name="x" size={16} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      type="text"
                      placeholder="Name"
                      value={member?.name}
                      onChange={(e) => handleMemberChange(index, 'name', e?.target?.value)}
                    />
                    <Input
                      type="text"
                      placeholder="Role (e.g., Mom, Dad, Son)"
                      value={member?.role}
                      onChange={(e) => handleMemberChange(index, 'role', e?.target?.value)}
                    />
                  </div>
                  <div>
                    <span className="text-sm text-gray-600 mb-2 block">Color</span>
                    <div className="flex gap-2 flex-wrap">
                      {colorOptions?.map((color) => (
                        <button
                          key={color?.value}
                          onClick={() => handleMemberChange(index, 'color', color?.value)}
                          className={`w-10 h-10 rounded-lg transition-all ${
                            member?.color === color?.value
                              ? 'ring-2 ring-offset-2 ring-blue-500 scale-110' :'hover:scale-105'
                          }`}
                          style={{ backgroundColor: color?.value }}
                          title={color?.label}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={handleAddMember}
                iconName="plus"
                iconPosition="left"
                fullWidth
              >
                Add Another Member
              </Button>
            </div>
          )}

          {wizardStep === 2 && (
            <div className="text-center space-y-6">
              <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-emerald-400 to-blue-500 rounded-full shadow-warm-xl animate-bounce">
                <Icon name="check" size={48} className="text-white" />
              </div>
              <div>
                <h3 className="text-h3 font-heading text-gray-900 mb-2">
                  Welcome, {familyData?.familyName}!
                </h3>
                <p className="text-gray-600">
                  You've added {familyData?.members?.length} family member{familyData?.members?.length !== 1 ? 's' : ''}.
                  You're all set to start organizing your family life!
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Next steps:</strong> Explore the calendar to add your first activity,
                  or visit settings to customize notifications and preferences.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Wizard Navigation */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
          <Button
            variant="ghost"
            onClick={onSkip}
          >
            Skip Setup
          </Button>
          <div className="flex gap-3">
            {wizardStep > 0 && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                iconName="chevron-left"
                iconPosition="left"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canProceed()}
              iconName={wizardStep === wizardSteps?.length - 1 ? 'check' : 'chevron-right'}
              iconPosition="right"
              variant={wizardStep === wizardSteps?.length - 1 ? 'success' : 'default'}
            >
              {wizardStep === wizardSteps?.length - 1 ? 'Get Started' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStartWizard;